const defaultOptions = {
  accessors: [(v) => v],
  reverse: false
};

const prioritySortDefaultOptions = {
  ...defaultOptions,
  reversePropagate: true
};

/*
  HOF that returns a function that sorts two elements, a and b,
  alphabetically. It can receive two options:

  - accessor: A function that returns the actual value that we
    want to use to perform the sorting algorithm. The default
    is the identity function.
  - reverse: A boolean to invert the sorting direction. False
    by default.
*/
export function alphaSort(options) {
  const mergedOptions = {
    ...defaultOptions,
    ...options
  };

  const { accessors, reverse } = mergedOptions;
  const [accessor, ...otherAccessors] = accessors;

  return (a, b) => {
    if (accessor(a) === undefined && accessor(b) === undefined) {
      return 0;
      // eslint-disable-next-line no-else-return
    } else if (accessor(a) === undefined) {
      return 1;
      // eslint-disable-next-line no-else-return
    } else if (accessor(b) === undefined) {
      return -1;
    }

    const aNormalized = String(accessor(a)).toLocaleUpperCase();
    const bNormalized = String(accessor(b)).toLocaleUpperCase();
    const multiplier = reverse ? -1 : 1;

    // If the elements are equal, we need to sort by the next accessor
    if (aNormalized.localeCompare(bNormalized) === 0 && accessors.length > 1) {
      return alphaSort({ ...options, accessors: otherAccessors })(a, b);
    }

    // Avoid -0
    if (aNormalized.localeCompare(bNormalized) === 0) {
      return 0;
    }

    return aNormalized.localeCompare(bNormalized) * multiplier;
  };
}

/*
  HOF that returns a function to sort two elements, setting always first
  those elements that are equal to topElement. The rest of elements
  will be sorted alphametically using the alphaSort HOF.

  This function receives two options:
  - accessor: A function that returns the actual value that we
    want to use to perform the sorting algorithm. The default
    is the identity function.
  - topElement: Element that must be set on first all the time
  - reverse: Sets the topElement on at the end instead of at the beginning.
    Defaults to false.

  All passed options are passed to the alphaSort function as well. If you
  don't want to propagate the reverse boolean to the alphaSort function,
  you can pass an option called reversePropagate to false (by default it
  is true).
*/
export function prioritySort(options) {
  const mergedOptions = {
    ...prioritySortDefaultOptions,
    ...options
  };

  const alphaSortOptions = {
    ...defaultOptions,
    ...options,
    reverse: mergedOptions.reversePropagate && mergedOptions.reverse
  };

  if (!mergedOptions.topElement) {
    throw new Error(
      'Top element has not been defined. Did you forget to pass topElement?'
    );
  }

  const { topElement, accessors, reverse } = mergedOptions;
  const accessor = accessors[0];
  const alphaSorter = alphaSort(alphaSortOptions);
  const topElementNormalized = topElement.toLocaleUpperCase();

  return (a, b) => {
    const aNormalized = accessor(a).toLocaleUpperCase();
    const bNormalized = accessor(b).toLocaleUpperCase();

    if (aNormalized === topElementNormalized) return reverse ? 1 : -1;
    if (bNormalized === topElementNormalized) return reverse ? -1 : 1;
    return alphaSorter(a, b);
  };
}
